import { cn } from '@/utils';
import { Phone, Activity, CheckCircle, Clock, AlertCircle } from 'lucide-react';

interface LoadingPageProps {
  message?: string;
  variant?: 'default' | 'minimal' | 'full';
}

export function LoadingPage({ message = 'Loading your dashboard...', variant = 'default' }: LoadingPageProps) {
  if (variant === 'minimal') {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="relative">
          <div className="w-8 h-8 border-3 border-primary/20 rounded-full" />
          <div className="absolute top-0 left-0 w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (variant === 'full') {
    return (
      <div className={cn(
        'min-h-screen flex flex-col items-center justify-center transition-colors duration-300 p-6',
        'bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 dark:from-slate-950 dark:via-slate-900 dark:to-slate-800'
      )}>
        {/* Animated Logo Container */}
        <div className="relative mb-8">
          {/* Outer ring */}
          <div className="w-24 h-24 rounded-full border-4 border-primary/20 animate-pulse" />
          
          {/* Middle ring */}
          <div className="absolute top-0 left-0 w-24 h-24 rounded-full border-4 border-primary/40 border-t-transparent animate-spin" style={{ animationDuration: '1.5s' }} />
          
          {/* Inner ring */}
          <div className="absolute top-2 left-2 w-20 h-20 rounded-full border-4 border-primary/60 border-t-transparent animate-spin" style={{ animationDuration: '1s', animationDirection: 'reverse' }} />
          
          {/* Center icon */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="p-3 bg-primary rounded-xl shadow-lg">
              <Phone className="h-8 w-8 text-primary-foreground" />
            </div>
          </div>
        </div>

        {/* App Name */}
        <h1 className="text-2xl font-bold text-foreground mb-2">Call Records</h1>
        <p className="text-muted-foreground mb-8">{message}</p>

        {/* Loading Steps */}
        <div className="flex flex-col items-center gap-3 w-full max-w-xs">
          <LoadingStep icon={Activity} label="Initializing application" delay={0} />
          <LoadingStep icon={CheckCircle} label="Loading records" delay={200} />
          <LoadingStep icon={Clock} label="Preparing dashboard" delay={400} />
          <LoadingStep icon={AlertCircle} label="Almost ready" delay={600} />
        </div>

        {/* Progress bar */}
        <div className="mt-8 w-full max-w-xs">
          <div className="h-1 bg-primary/20 rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full animate-progress" />
          </div>
        </div>

        {/* Footer */}
        <p className="mt-8 text-xs text-muted-foreground">Powered by CodeCraft Innovations WLL</p>
      </div>
    );
  }

  // Default variant - balanced loading screen
  return (
    <div className={cn(
      'min-h-screen flex flex-col items-center justify-center transition-colors duration-300',
      'bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-800'
    )}>
      {/* Animated Icon */}
      <div className="relative mb-6">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary/60 shadow-lg flex items-center justify-center">
          <Phone className="h-8 w-8 text-primary-foreground" />
        </div>
        
        {/* Decorative elements */}
        <div className="absolute -top-2 -right-2 w-5 h-5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <div className="absolute -bottom-1 -left-2 w-4 h-4 bg-amber-500 rounded-full animate-bounce" style={{ animationDelay: '200ms' }} />
        <div className="absolute top-0 -left-3 w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '400ms' }} />
      </div>

      {/* Title */}
      <h1 className="text-xl font-semibold text-foreground mb-2">Call Records</h1>
      
      {/* Message with animated dots */}
      <p className="text-muted-foreground mb-6">
        <span>{message}</span>
        <span className="inline-flex w-12">
          <span className="animate-dot1">.</span>
          <span className="animate-dot2">.</span>
          <span className="animate-dot3">.</span>
        </span>
      </p>

      {/* Simple progress bar */}
      <div className="w-48 h-1.5 bg-primary/20 rounded-full overflow-hidden">
        <div className="h-full bg-primary rounded-full animate-pulse-width" />
      </div>
    </div>
  );
}

// Loading step component with staggered animation
function LoadingStep({ icon: Icon, label, delay }: { icon: typeof Phone; label: string; delay: number }) {
  return (
    <div 
      className="flex items-center gap-3 text-sm text-muted-foreground opacity-0 animate-fade-in"
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'forwards' }}
    >
      <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center animate-pulse">
        <Icon className="h-3 w-3 text-primary" />
      </div>
      <span>{label}</span>
    </div>
  );
}

// CSS for custom animations
export const loadingStyles = `
  @keyframes dot1 {
    0%, 100% { opacity: 0; transform: translateY(0); }
    33% { opacity: 1; transform: translateY(-4px); }
  }
  @keyframes dot2 {
    0%, 100% { opacity: 0; transform: translateY(0); }
    33% { opacity: 1; transform: translateY(-4px); }
    66% { opacity: 0; transform: translateY(0); }
  }
  @keyframes dot3 {
    0%, 100% { opacity: 1; transform: translateY(-4px); }
    33% { opacity: 0; transform: translateY(0); }
    66% { opacity: 1; transform: translateY(-4px); }
  }
  @keyframes pulse-width {
    0%, 100% { width: 20%; }
    50% { width: 80%; }
  }
  @keyframes progress {
    0% { width: 0%; margin-left: 0; }
    100% { width: 100%; margin-left: 0; }
  }
  @keyframes fade-in {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .animate-dot1 { animation: dot1 1.5s infinite; }
  .animate-dot2 { animation: dot2 1.5s infinite; }
  .animate-dot3 { animation: dot3 1.5s infinite; }
  .animate-pulse-width { animation: pulse-width 2s ease-in-out infinite; }
  .animate-progress { animation: progress 3s ease-out forwards; }
  .animate-fade-in { animation: fade-in 0.5s ease-out forwards; }
  .animate-pulse-soft { animation: pulse-soft 2s ease-in-out infinite; }
  
  @keyframes pulse-soft {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`;
